import { PublicClientApplication } from '@azure/msal-browser';
import type { AuthStateUpdate, SetGlobalDataCache } from 'App/AppContextProvider';
import { IdentityProviderType } from 'models/identityProvider';
import Tenant from 'models/tenant';
import { IAuthProvider } from './authProvider';
import authService from './authService';

//
// Microsoft Entra ID through MSAL: binds the client application to the functions of authService, which
// stay as they are so that the Entra sign-in is unchanged.
//
export class MsalAuthProvider implements IAuthProvider {
  readonly providerType = IdentityProviderType.Entra;

  private readonly publicClientApplication: PublicClientApplication;

  constructor(publicClientApplication: PublicClientApplication) {
    this.publicClientApplication = publicClientApplication;
  }

  initialize = (): Promise<void> => {
    return this.publicClientApplication.initialize();
  };

  //MSAL completes its own redirect landing inside handleRedirectPromise; there is no url shape to detect here
  isRedirectCallback = (): boolean => {
    return false;
  };

  handleRedirectResponse = (
    authStateUpdate: AuthStateUpdate,
    setGlobalDataCache: SetGlobalDataCache,
  ): Promise<boolean> => {
    return authService.handleRedirectResponse(this.publicClientApplication, authStateUpdate, setGlobalDataCache);
  };

  ssoSilent = (
    directoryId: string | undefined,
    authStateUpdate: AuthStateUpdate,
    setGlobalDataCache: SetGlobalDataCache,
    allowInteraction: boolean = true,
  ): Promise<void> => {
    return authService.ssoSilent(this.publicClientApplication, directoryId, authStateUpdate, setGlobalDataCache, allowInteraction);
  };

  login = (
    directoryId: string | undefined,
    authStateUpdate: AuthStateUpdate,
    setGlobalDataCache: SetGlobalDataCache,
  ): Promise<void> => {
    return authService.login(this.publicClientApplication, directoryId, authStateUpdate, setGlobalDataCache);
  };

  logout = (authStateUpdate: AuthStateUpdate): Promise<void> => {
    return authService.logout(this.publicClientApplication, authStateUpdate);
  };

  switchTenant = (
    tenant: Tenant,
    authStateUpdate: AuthStateUpdate,
    setGlobalDataCache: SetGlobalDataCache,
  ): Promise<void> => {
    return authService.switchTenant(this.publicClientApplication, authStateUpdate, setGlobalDataCache, tenant);
  };

  setUserProfile = (authStateUpdate: AuthStateUpdate, setGlobalDataCache: SetGlobalDataCache): Promise<void> => {
    return authService.setUserProfile(this.publicClientApplication, authStateUpdate, setGlobalDataCache);
  };

  getAccessToken = (scopes: string[], tenant?: Tenant, allowInteraction?: boolean): Promise<string> => {
    return authService.getAccessToken(this.publicClientApplication, scopes, tenant, allowInteraction);
  };

  hasScopes = (scopes: string[], tenant?: Tenant): Promise<boolean> => {
    return authService.hasScopes(this.publicClientApplication, scopes, tenant);
  };

  adminConsentScope = (
    clientId: string,
    scopes: string,
    authStateUpdate: AuthStateUpdate,
    redirectUrl: string,
    stateSuffix?: string,
  ): void => {
    authService.adminConsentScope(clientId, scopes, this.publicClientApplication, authStateUpdate, redirectUrl, stateSuffix);
  };

  getActiveDirectoryId = (): string | undefined => {
    return this.publicClientApplication.getActiveAccount()?.tenantId;
  };
}
